import { Tag, TagLabel, TagCloseButton, Box, Input, Flex, Text, VStack } from "@chakra-ui/react";
import isValidDomain from 'is-valid-domain';
import { FC, useRef, useState, useMemo } from "react";
import { useTeamInvites, useToastNotification } from "lib/hooks";
import { teamInvitesClient } from "lib/clients";
import {TeamInviteType} from "lib/types";
import { v4 as uuid } from 'uuid';

type Props = {
  setInvites: any;
  invites: any;
}

// What keys in the input will let us know the user has finished typing a domain
const TAG_TRIGGERS = [',', ' '];

export const ManageDomainInvites: FC<Props> = ({ setInvites, invites }) => {
  const [state, setState] = useState<any>({
    inputValue: '',
    isInputFocused: false,
  });
  const { data, mutate } = useTeamInvites();

  const domainInvites = useMemo(() => {
    return data
      .filter((invite) => invite.type === TeamInviteType.GROUP)
      .sort((a, b) => {
        if (a.match < b.match) { return -1; }
        if (a.match > b.match) { return 1; }
        return 0;
      });
  }, [data]);

  const ref = useRef(null);
  const { successToast, errorToast } = useToastNotification();

  const wrapperOnClick = () => {
    ref?.current?.focus();
    setState({ ...state, isInputFocused: true });
  }

  const onChange = ({ currentTarget: { value } }) => {
    // We have a controlled input, so we need to update the state
    setState({ ...state, inputValue: value });
    setInvites({ ...invites, pendingDomain: value });

    const lastKey = value[value.length - 1]

    // If the key is not in the triggers we don't need to do anything
    if (!TAG_TRIGGERS.includes(lastKey)) return;

    // We need to remove the trigger character,
    // Otherwise the domain wont be valid
    const cleanedDomain = value.substring(0, value.length - 1);

    // User has typed a trigger key, so check if it's a valid domain
    // If it's not do nothing
    if (!isValidDomain(cleanedDomain)) return;

    // Check if domain is already in pendingInvites
    // If it is, do nothing
    if (invites.pendingDomainInvites.includes(cleanedDomain)) return;

    // If we reach this point we have a valid unique domain

    setInvites({
      ...invites,
      pendingDomain: '',
      pendingDomainInvites: [ ...invites.pendingDomainInvites, cleanedDomain ],
    })

    setState({ ...state, inputValue: '' });
  }

  const removeSentInvite = async (invite): Promise<void> => {
    try {
      await teamInvitesClient.deleteInvite(invite.id);
      await mutate();

      successToast({
        title: 'Success',
        description: `Invite removed for ${invite.match}`,
      });
    } catch (err) {

      errorToast({
        title: 'Error',
        description: `Could not remove for ${invite.match}`,
      });
    }
  }

  const removePendingInvite = (domain: string): void => {
    const clean = invites.pendingDomainInvites.filter((invite) => invite !== domain);
    setInvites({ ...invites, pendingDomainInvites: [ ...clean ] })
  }

  return (
    <VStack spacing="10px" alignItems="start" width="100%">
      <Text variant="label" size="sm">Approve team invitations for any email address containing these domains:</Text>
      <Box onClick={wrapperOnClick} minHeight="80px" width="100%" className={`${state.isInputFocused ? 'input-tags input-tags--active' : 'input-tags' } `}>
        <Flex gap="6px" flexWrap="wrap" gridGap="6px">
          
          {/* Already sent invites */}
          {domainInvites.map((invite) => (
            <Box key={invite.id}>
              <Tag variant="solid" size="md">
                <TagLabel>{invite.match}</TagLabel>
                <TagCloseButton onClick={() => removeSentInvite(invite)} />
              </Tag>
            </Box>
          ))}

          {/* Invites that have not yet been sent */}
          {invites.pendingDomainInvites?.map((invite: string) => (
            <Box key={uuid()}>
              <Tag variant="solid" size="md">
                <TagLabel>{invite}</TagLabel>
                <TagCloseButton onClick={() => removePendingInvite(invite)} />
              </Tag>
            </Box>
          ))}

          <Box>
            <Input
              size="sm"
              ref={ref}
              variant="readonly"
              type="text"
              placeholder="Enter domains"
              onChange={onChange}
              height="24px"
              onBlur={() => setState({ ...state, isInputFocused: false })}
              width="auto"
              value={state.inputValue}
            />
          </Box>
        </Flex>
      </Box>
    </VStack>
  )
};
